import mapValues from "lodash/mapValues";
import moment from "moment-timezone";
import { toPng } from "html-to-image";
import util from "@/assets/js/util";

export const capitalized = (name, preserveLiteral) => {
  if (!name) return name;
  if (!preserveLiteral) {
    return name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return name
    .split(/(\s+)/)
    .map((segment) => {
      if (/^\s+$/.test(segment)) return segment;
      return segment.toLowerCase() === preserveLiteral.toLowerCase()
        ? preserveLiteral
        : segment.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    })
    .join("");
};

export const kualifikasiUsaha = (key) => {
  switch (key) {
    case 1:
      return "Kecil";
    case 2:
      return "Sedang";
    case 3:
      return "Besar";
    default:
      return "-";
  }
};

export const tingkatResiko = (key) => {
  switch (key) {
    case 1:
      return "Rendah";
    case 2:
      return "Menengah Rendah";
    case 3:
      return "Menengah Tinggi";
    case 4:
      return "Tinggi";
    default:
      return "-";
  }
};

export const kontraktorLokasi = (name) => {
  if (!name) return "-";
  return name.toLowerCase().trim() === "kalimantan selatan"
    ? "Lokal"
    : "Non Lokal";
};

export const changeTypeEnum = {
  PERPANJANG: "PERPANJANG",
  PERGANTIAN: "PERGANTIAN",
  UPDATEDATA: "UPDATEDATA",
};

export const replaceNullWithEmptyString = (obj) => {
  return mapValues(obj, (value) => {
    if (value === null) return "";
    if (typeof value === "object") {
      return replaceNullWithEmptyString(value); // rekursif untuk objek bersarang
    }
    return value;
  });
};

export const formatNumberToString = (value) => {
  let [integerPart, decimalPart] = value
    .toFixed(2)
    .replace(".", ",")
    .split(",");

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return decimalPart ? `${integerPart},${decimalPart}` : integerPart;
};

export const formatStringToNumber = (value) => {
  if (typeof value !== "string") return value;
  return parseFloat(value.replace(/\./g, "").replace(",", "."));
};

export const formatNumberFlexible = (value) => {
  if (typeof value !== "number") {
    value = parseFloat(value);
  }
  if (isNaN(value)) return "-";

  const parts = value.toString().split(".");
  let integerPart = parts[0];
  const decimalPart = parts[1];

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return decimalPart ? `${integerPart},${decimalPart}` : integerPart;
};

/** Ribuan `.`, pecahan `,` (locale Indonesia), hingga 2 digit desimal. */
export const formatNumberId = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

/**
 * Parse angka dari format id-ID (`"73,5"`, `"1.234,56"`) maupun angka biasa.
 * Mengembalikan `NaN` jika nilai tidak bisa di-parse.
 */
export const parseNumberId = (value) => {
  if (value === null || value === undefined || value === "") {
    return NaN;
  }
  if (typeof value === "number") {
    return value;
  }
  const raw = String(value).trim();
  if (!raw) return NaN;
  // Sudah dalam format JS valid (mis. "73.5" atau "12")
  if (/^-?\d+(\.\d+)?$/.test(raw)) {
    return Number(raw);
  }
  // Buang titik ribuan, ubah koma desimal menjadi titik
  const normalized = raw.replace(/\./g, "").replace(",", ".");
  return Number(normalized);
};

export const formatDateToCustomText = (dateString) => {
  const date = moment(dateString).tz("Asia/Singapore");
  const timeFormatted = date.format("HH.mm");
  const label = date.fromNow();

  return `${timeFormatted} - ${label}`;
};

export const formatFloat = (value, defaultPrecision = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "0";
  }

  const number = parseFloat(value);

  const toDecimalString = (num) => {
    let str = String(num);
    if (str.includes("e")) {
      let [mantissa, exponent] = str.split("e");
      exponent = parseInt(exponent, 10);

      if (exponent < 0) {
        const zeros = Math.abs(exponent) - 1;
        mantissa = mantissa.replace(".", "");
        str = "0." + "0".repeat(zeros) + mantissa;
      } else {
        const [intPart, decPart = ""] = mantissa.split(".");
        str = intPart + (decPart + "0".repeat(exponent)).slice(0);
      }
    }
    return str;
  };

  if (Number.isInteger(number)) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  if (Math.abs(number) < 1 && number !== 0) {
    const str = toDecimalString(number);
    const match = str.match(/0\.0*(\d)/);

    if (match) {
      const leadingZeros = match[0].split("0").length - 2 || 0;
      const precision = leadingZeros + 1;
      const [intPart, decPart] = number.toFixed(precision).split(".");

      return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "," + decPart;
    }
  }

  const [intPart, decPart] = number.toFixed(defaultPrecision).split(".");

  let result = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  if (decPart) {
    result += "," + decPart;
  }

  result = result.replace(/,00$/, "").replace(/,\.?$/, "");

  return result;
};

export const fixDate = (date) => {
  if (!date) return null;
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

export function formatFileSize(size) {
  if (typeof size !== "number" || isNaN(size) || size <= 0) {
    return "0 KB";
  }

  const kb = size / 1024;
  const mb = kb / 1024;

  if (mb >= 1) {
    return `${parseFloat(mb.toFixed(2))} MB`;
  }

  return `${parseFloat(kb.toFixed(2))} KB`;
}

// Container max-w-72 (288px) + text-xs + line-clamp-3.
// isLong = true ketika perkiraan baris tampilan > 3 (ada ...).
// Kalibrasi 1 line: "...211" (41 char) = 1 baris, "...212" (42 char) = wrap.
const CHARS_PER_LINE_SINGLE = 38; // 1 line: ≤114 = 3 baris (false), 115+ = 4 baris (true)
const CHARS_PER_LINE_288 = 36; // untuk 2 baris (teks dengan \n)
const CHARS_PER_LINE_288_MULTI = 39; // 3+ baris, selaras dengan SINGLE
const WRAP_OVER = 38; // baris >38 char (3+ baris) → pakai 36 agar wrap terdeteksi

export const isLong = (text) => {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  const lineLengths = trimmed.split(/\r?\n/).map((l) => l.length);
  const isSingle = lineLengths.length === 1;
  const isMulti = lineLengths.length >= 3;
  const totalDisplayLines = lineLengths.reduce((sum, lineLen) => {
    const useStrict = isMulti && lineLen > WRAP_OVER;
    const cpl = isSingle
      ? CHARS_PER_LINE_SINGLE
      : useStrict
      ? CHARS_PER_LINE_288
      : isMulti
      ? CHARS_PER_LINE_288_MULTI
      : CHARS_PER_LINE_288;
    return sum + Math.ceil(lineLen / cpl);
  }, 0);
  return totalDisplayLines > 3;
};

export const isCatatanLong = (text) => {
  if (!text) return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  const lineLengths = trimmed.split(/\r?\n/).map((l) => l.length);
  const totalDisplayLines = lineLengths.reduce((sum, lineLen) => {
    return sum + Math.max(1, Math.ceil(lineLen / 36));
  }, 0);
  return totalDisplayLines > 2;
};

export const ncDescWithBreaks = (text) => {
  if (text == null || text === "") return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML.replace(/\r?\n/g, "<br>");
};

export const getPaddingBottomFromDescriptions = (items) => {
  const labelSpace = 32;
  const maxWidthPx = 112; // w-24
  const charsPerLineLabel = Math.max(1, Math.floor(maxWidthPx / 6));
  const lineHeightLabel = 14;
  const charsPerLineDesc = Math.max(1, Math.floor(maxWidthPx / 5));
  const lineHeightDesc = 12;

  let maxContentHeightPx = 0;
  let hasAnyLabel = false;
  for (const item of items) {
    const labelLen = (item.label || "").length;
    const descLen = (item.description || "").length;
    if (labelLen > 0) hasAnyLabel = true;
    const labelLines = Math.ceil(labelLen / charsPerLineLabel);
    const descLines = Math.ceil(descLen / charsPerLineDesc);
    const itemHeightPx =
      labelLines * lineHeightLabel + descLines * lineHeightDesc;
    if (itemHeightPx > maxContentHeightPx) maxContentHeightPx = itemHeightPx;
  }
  if (maxContentHeightPx === 0 && hasAnyLabel)
    maxContentHeightPx = lineHeightLabel;
  if (maxContentHeightPx === 0) return 0;

  if (maxContentHeightPx <= lineHeightLabel) return 28;

  const total = Math.ceil(labelSpace + maxContentHeightPx);
  return Math.max(total, 56);
};

/**
 * Unduh elemen DOM (by id) sebagai gambar PNG.
 * @param {string} elementId - ID elemen yang akan di-capture
 * @param {Object} [options]
 * @param {string} [options.fileName] - Nama file dasar (tanpa ekstensi); hasil: fileName_tanggal.png
 * @param {string[]} [options.fileNameParts] - Bagian nama file; hasil: fileNameParts.join('_') + '_' + tanggal.png (tanpa elementId)
 * @param {string} [options.successMessage='Gambar dashboard berhasil diunduh.'] - Pesan sukses notifikasi
 */
export async function downloadElementAsPng(elementId, options = {}) {
  const {
    fileName,
    fileNameParts,
    successMessage = "Gambar dashboard berhasil diunduh.",
  } = options;

  const node = document.getElementById(elementId);
  if (!node) {
    util.showError(new Error("Element tidak ditemukan"));
    return;
  }

  try {
    // Firefox: CSSFontFaceRule.style.fontFamily can be undefined → html-to-image's
    // normalizeFontFamily calls .trim() and throws. Chrome exposes a string.
    const toPngOptions = {
      quality: 1,
      pixelRatio: 2,
      ...(typeof navigator !== "undefined" &&
      /firefox/i.test(navigator.userAgent)
        ? { skipFonts: true }
        : {}),
    };
    const dataUrl = await toPng(node, toPngOptions);
    const link = document.createElement("a");
    link.href = dataUrl;
    const formattedDate = new Date()
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, "");

    let baseName;
    if (fileName != null && fileName !== "") {
      baseName = fileName;
    } else if (fileNameParts && fileNameParts.length > 0) {
      baseName = fileNameParts.filter(Boolean).join("_");
    } else {
      baseName = elementId;
    }
    link.download = `${baseName}_${formattedDate}.png`;
    link.click();
    util.showInfo(successMessage);
  } catch (err) {
    console.error(err);
    util.showError(err);
  }
}

export const convertScoreToClass = (score) => {
  if (score > 90) return "bg-yellow-600";
  if (score > 80) return "bg-green-700";
  if (score >= 65) return "bg-blue-600";
  if (score > 50) return "bg-red-600";
  return "bg-black";
};

/** Hex chart color selaras kategori `convertScoreToClass`. */
export const convertScoreToColor = (score) => {
  if (score > 90) return "#CA8A04";
  if (score > 80) return "#15803D";
  if (score >= 65) return "#2563EB";
  if (score > 50) return "#DC2626";
  return "#000000";
};

/**
 * Warna halo / cincin luar yang selaras skor dengan `convertScoreToClass`, tapi shade lebih terang
 * (mis. `bg-blue-600` → `bg-blue-200`) supaya kontras dengan lingkaran dalam berisi angka.
 */
export const convertScoreToLightRingClass = (score) => {
  if (score > 90) return "bg-yellow-200";
  if (score > 80) return "bg-green-200";
  if (score >= 65) return "bg-blue-200";
  if (score > 50) return "bg-red-200";
  return "bg-gray-200";
};

export const SCORE_GRADES = ["Emas", "Hijau", "Biru", "Merah", "Hitam"];

export const SCORE_GRADE_COLORS = {
  Emas: "#CA8A04",
  Hijau: "#15803D",
  Biru: "#2563EB",
  Merah: "#DC2626",
  Hitam: "#000000",
};

/** Map predikat → 0, untuk agregasi jumlah per kategori skor. */
export const createEmptyGradeCounts = () => {
  return Object.fromEntries(SCORE_GRADES.map((grade) => [grade, 0]));
};

export const convertGradeToColor = (grade) => {
  return SCORE_GRADE_COLORS[grade] ?? "#000000";
};

export const convertScoreToGrade = (score) => {
  if (score > 90) return "Emas";
  if (score > 80) return "Hijau";
  if (score >= 65) return "Biru";
  if (score > 50) return "Merah";
  return "Hitam";
};
